/* reviews-carousel-trautis.js
 * Native homepage "Was Kunden sagen" carousel, rendered from Judge.me's PUBLIC,
 * no-auth, CORS-open carousel endpoint (cdn.judge.me/reviews/reviews_for_carousel,
 * reviews_selection=all). Replaces the off-brand Judge.me @app testimonials widget
 * with a brand-native scroll-snap rail (same mechanics as the Bestseller carousel).
 *
 * Provenance labelling (§5b UWG): each card is tagged from verified_buyer —
 *   true  -> "Verifizierter Kauf"           (review_collected_via_store_invitation)
 *   false -> "Importiert · nicht verifiziert" (review_collected_from_another_provider)
 * The aggregate header (avg ★ · aus N) is computed over the VERIFIED subset only.
 *
 * Review photos are intentionally NOT rendered (imported reviews link to a foreign
 * alicdn CDN). Bodies are rendered as PLAIN TEXT via DOMParser (inert document) so a
 * review body cannot trigger image/pixel fetches or inject markup. innerHTML below is
 * XSS-safe by construction: every API value is esc()'d or coerced to Number().
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.__trautisReviewsLoaded) return;
  if (typeof window !== 'undefined') window.__trautisReviewsLoaded = true;

  var ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_carousel';

  var _pool = {}; // page-level dedupe: ein Promise je shop|sr

  function buildUrl(shop, sr) {
    return ENDPOINT
      + '?reviews_selection=all&carousel_type=testimonials'
      + '&star_rating=' + encodeURIComponent(sr || 'all')
      + '&max_reviews=100'
      + '&url=' + encodeURIComponent(shop)
      + '&shop_domain=' + encodeURIComponent(shop)
      + '&platform=shopify&primary_language=de';
  }

  function fetchReviews(shop, sr) {
    var key = shop + '|' + (sr || 'all');
    if (_pool[key]) return _pool[key];
    var p = fetch(buildUrl(shop, sr), { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) { return (data && data.reviews) || []; });
    p.catch(function () { delete _pool[key]; }); // Fehler → retry beim nächsten Aufruf erlauben
    _pool[key] = p;
    return p;
  }

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function starsHtml(rating, label) {
    var n = Math.round(Number(rating) || 0);
    var out = '<span class="t-reviewcard__stars"' + (label ? ' aria-label="' + esc(label) + '"' : '') + '>';
    for (var i = 1; i <= 5; i++) {
      out += '<span class="t-reviewcard__star' + (i <= n ? '' : ' t-reviewcard__star--off') + '" aria-hidden="true">★</span>';
    }
    return out + '</span>';
  }

  function fmtDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return ''; }
  }

  // Judge.me body is untrusted user text. Render as plain text (preserve line breaks),
  // never as live HTML. DOMParser('text/html') builds an inert document — runs no
  // scripts, loads no subresources (prevents the imported-review alicdn pixel leak).
  // Decodes API HTML-entities to plain text. Judge.me serves product_title/title
  // ALREADY HTML-encoded (e.g. &quot;Böser Blick&quot;); a plain esc() on top would
  // double-encode and the card would show a literal "&quot;". So decode first, then
  // re-esc() at insertion. DOMParser('text/html') is inert (no scripts, no subresource
  // loads), so this stays XSS-safe.
  function decode(s) {
    if (s == null || s === '') return '';
    var doc = new DOMParser().parseFromString(String(s), 'text/html');
    return (doc.body && doc.body.textContent) || '';
  }

  // Raw Judge.me body_html -> plain text with \n line breaks (inert parse, see decode()).
  function bodyText(html) {
    var s = (html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
    var doc = new DOMParser().parseFromString(s, 'text/html');
    return ((doc.body && doc.body.textContent) || '').trim();
  }

  // Plain text -> safe inner HTML: esc() everything, then re-introduce <br> only.
  function textToHtml(text) {
    if (!text) return '';
    return esc(text).replace(/\n{2,}/g, '<br><br>').replace(/\n/g, '<br>');
  }

  function bodyHtml(html) { return textToHtml(bodyText(html)); }

  // Max length for promoting a review's first body line to a headline (Option A).
  // Short, headline-like first lines become the upright title; longer first lines
  // (full sentences) stay part of the italic quote, so we never fake a "title".
  var HEADLINE_MAX = 48;

  function sortReviews(reviews) {
    return reviews.slice().sort(function (a, b) {
      var ap = a.pinned ? 1 : 0, bp = b.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;
      var av = a.verified_buyer ? 1 : 0, bv = b.verified_buyer ? 1 : 0;
      if (av !== bv) return bv - av;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }

  function aggregate(reviews) {
    var v = reviews.filter(function (r) { return r.verified_buyer === true; });
    if (!v.length) return null;
    var sum = v.reduce(function (s, r) { return s + (Number(r.rating) || 0); }, 0);
    return { count: v.length, avg: sum / v.length };
  }

  function productRef(rev) {
    var title = decode(rev.product_title || '').trim();
    var url = (rev.product_url || '').trim();
    if (!title || url === '/') return '';
    if (rev.product_in_store === true && url.indexOf('/products/') === 0) {
      return '<a class="t-reviewcard__product" href="' + esc(url) + '">zu ' + esc(title) + '</a>';
    }
    return '<span class="t-reviewcard__product t-reviewcard__product--plain">zu ' + esc(title) + '</span>';
  }

  // Verifiziert-Siegel: scalloped Rosette (fill = currentColor → Trust-Grün der Pille)
  // mit weißem Haken. Inline-SVG statt ::before-Glyph → identisch in Karte UND
  // Trust-Streifen, crisp skalierbar, themebar über currentColor.
  function verifiedSealSvg() {
    return '<svg class="t-seal" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">'
      + '<path fill="currentColor" d="M12 1 L14.28 3.5 L17.5 2.47 L18.22 5.78 L21.53 6.5 L20.5 9.72 L23 12 L20.5 14.28 L21.53 17.5 L18.22 18.22 L17.5 21.53 L14.28 20.5 L12 23 L9.72 20.5 L6.5 21.53 L5.78 18.22 L2.47 17.5 L3.5 14.28 L1 12 L3.5 9.72 L2.47 6.5 L5.78 5.78 L6.5 2.47 L9.72 3.5 Z"/>'
      + '<path fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M7.8 12.4 11 15.4 16.3 9.2"/>'
      + '</svg>';
  }

  function badgeHtml(rev, labels) {
    if (rev.verified_buyer === true) {
      return '<span class="t-reviewcard__badge t-reviewcard__badge--verified">' + verifiedSealSvg() + esc(labels.verified) + '</span>';
    }
    return '<span class="t-reviewcard__badge t-reviewcard__badge--imported">' + esc(labels.imported) + '</span>';
  }

  function cardHtml(rev, labels) {
    var name = (rev.is_anonymous_reviewer || !rev.reviewer_name) ? 'Anonym' : rev.reviewer_name;
    var date = rev.created_at ? '<time class="t-reviewcard__date">' + esc(fmtDate(rev.created_at)) + '</time>' : '';
    // Headline/Body-Hierarchie: ein echter Judge.me-Titel hat Vorrang. Fehlt er (die
    // meisten Reviews haben keinen → "Titel" und "Body" sahen vorher identisch aus),
    // wird eine KURZE erste Body-Zeile zur Headline befördert, der Rest bleibt Zitat.
    var titleText = decode(rev.title || '').trim();
    var bText = bodyText(rev.body_html || rev.body);
    if (!titleText) {
      var nl = bText.search(/\n/);
      if (nl > 0) {
        var first = bText.slice(0, nl).trim();
        var rest = bText.slice(nl).trim();
        if (first.length <= HEADLINE_MAX && rest) { titleText = first; bText = rest; }
      }
    }
    var title = titleText ? '<p class="t-reviewcard__title">' + esc(titleText) + '</p>' : '';
    var body = textToHtml(bText);
    return '<article class="t-reviewcard" role="listitem">'
      + '<div class="t-reviewcard__head">'
      + starsHtml(rev.rating, (Number(rev.rating) || 0) + ' von 5')
      + badgeHtml(rev, labels)
      + '</div>'
      + title
      + (body ? '<blockquote class="t-reviewcard__body">' + body + '</blockquote>' : '')
      + '<footer class="t-reviewcard__meta">'
      + '<span class="t-reviewcard__author">' + esc(name) + '</span>'
      + productRef(rev)
      + date
      + '</footer>'
      + '</article>';
  }

  function aggregateHtml(agg, suffix) {
    var avgStr = agg.avg.toFixed(1).replace('.', ',');
    return '<div class="t-reviews__aggregate">'
      + starsHtml(agg.avg, avgStr + ' von 5 Sternen')
      + '<span class="t-reviews__aggregate-avg">' + esc(avgStr) + '</span>'
      + '<span class="t-reviews__aggregate-count">· aus ' + agg.count + ' ' + esc(suffix) + '</span>'
      + '</div>';
  }

  // Click-and-drag to slide the rail (desktop pointers). Touch keeps native momentum
  // scroll, so we ignore touch pointers here. A real drag (moved past a small
  // threshold) swallows the trailing click so dragging over a product link/button
  // doesn't navigate.
  function wireDrag(rail) {
    var down = false, startX = 0, startLeft = 0, moved = 0;
    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      down = true; moved = 0; startX = e.clientX; startLeft = rail.scrollLeft;
      rail.classList.add('is-dragging');
    });
    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      rail.scrollLeft = startLeft - dx;
      e.preventDefault();
    });
    function end() { if (!down) return; down = false; rail.classList.remove('is-dragging'); }
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointerleave', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('click', function (e) {
      if (moved > 6) { e.preventDefault(); e.stopPropagation(); moved = 0; }
    }, true);
  }

  function wireArrows(el) {
    var rail = el.querySelector('.t-reviews__rail');
    if (!rail) return;
    wireDrag(rail);
    var prev = el.querySelector('.t-reviews__arrow--prev');
    var next = el.querySelector('.t-reviews__arrow--next');
    if (prev && next) {
      var step = function () {
        var c = rail.querySelector('.t-reviewcard');
        return (c ? c.getBoundingClientRect().width : 320) + 16;
      };
      prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
    }
    var sync = function () { el.classList.toggle('t-reviews--has-overflow', rail.scrollWidth > rail.clientWidth + 4); };
    rail.addEventListener('scroll', sync);
    sync();
  }

  function plainBody(rev) { return bodyText(rev.body_html || rev.body); }

  function stripPool(reviews) {
    return sortReviews(reviews.filter(function (r) {
      return r.verified_buyer === true && plainBody(r).length > 0;
    }));
  }

  function slotHtml(rev, labels) {
    var name = (rev.is_anonymous_reviewer || !rev.reviewer_name) ? 'Anonym' : rev.reviewer_name;
    return starsHtml(rev.rating, (Number(rev.rating) || 0) + ' von 5')
      + '<span class="t-trust-strip__quote">„' + esc(plainBody(rev)) + '"</span>'
      + '<span class="t-trust-strip__by">'
      + '<span class="t-trust-strip__author">' + esc(name) + '</span>'
      + '<span class="t-trust-strip__badge">' + verifiedSealSvg() + esc(labels.verified) + '</span>'
      + '</span>';
  }

  function stripHtml(reviews, opts) {
    var labels = { verified: (opts && opts.verified) || 'Verifizierter Kauf' };
    var pool = stripPool(reviews);
    if (!pool.length) return '';
    return '<div class="t-trust-strip__slot">' + slotHtml(pool[0], labels) + '</div>';
  }

  function renderStrip(el, reviews) {
    var labels = { verified: el.getAttribute('data-label-verified') || 'Verifizierter Kauf' };
    var note = el.getAttribute('data-note');
    var pool = stripPool(reviews);
    if (!pool.length) { el.hidden = true; return; }
    el.innerHTML = '<div class="t-trust-strip__slot">' + slotHtml(pool[0], labels) + '</div>'
      + (note ? '<p class="t-trust-strip__note">' + esc(note) + '</p>' : '');
    el.hidden = false;
    if (pool.length > 1) {
      var slot = el.querySelector('.t-trust-strip__slot');
      slot.style.transition = 'opacity .25s';
      var i = 0;
      var id = setInterval(function () {
        if (!el.isConnected) { clearInterval(id); return; }
        i = (i + 1) % pool.length;
        slot.style.opacity = '0';
        setTimeout(function () { slot.innerHTML = slotHtml(pool[i], labels); slot.style.opacity = '1'; }, 250);
      }, 4200);
    }
  }

  function failStrip(el) { el.hidden = true; }

  function loadStrip(el) {
    if (el.__loaded) return Promise.resolve();
    el.__loaded = true;
    var shop = el.getAttribute('data-shop');
    if (!shop) return Promise.resolve();
    var sr = el.getAttribute('data-star-rating') || 'all';
    return fetchReviews(shop, sr)
      .then(function (reviews) { renderStrip(el, reviews); })
      .catch(function () { failStrip(el); el.__loaded = false; });
  }

  function render(el, reviews) {
    var labels = {
      verified: el.getAttribute('data-label-verified') || 'Verifizierter Kauf',
      imported: el.getAttribute('data-label-imported') || 'Importiert · nicht verifiziert'
    };
    var max = parseInt(el.getAttribute('data-max'), 10) || 12;
    var sorted = sortReviews(reviews).slice(0, max);
    if (!sorted.length) { fail(el); return; }
    var html = '';
    if (el.getAttribute('data-show-aggregate') === 'true') {
      var agg = aggregate(reviews);
      if (agg) html += aggregateHtml(agg, el.getAttribute('data-aggregate-suffix') || 'verifizierten Bewertungen');
    }
    html += '<div class="t-reviews__viewport">'
      + '<button class="t-reviews__arrow t-reviews__arrow--prev" type="button" aria-label="Vorherige Bewertungen">‹</button>'
      + '<div class="t-reviews__rail" role="list">'
      + sorted.map(function (r) { return cardHtml(r, labels); }).join('')
      + '</div>'
      + '<button class="t-reviews__arrow t-reviews__arrow--next" type="button" aria-label="Nächste Bewertungen">›</button>'
      + '</div>';
    el.innerHTML = html;
    el.hidden = false;
    wireArrows(el);
  }

  function fail(el) {
    el.innerHTML = '<p class="t-reviews__empty">Echte Kundenstimmen folgen in Kürze.</p>';
    el.hidden = false;
  }

  function load(el) {
    if (el.__loaded) return Promise.resolve();
    el.__loaded = true;
    var shop = el.getAttribute('data-shop');
    if (!shop) return Promise.resolve();
    var sr = el.getAttribute('data-star-rating') || 'all';
    return fetchReviews(shop, sr)
      .then(function (reviews) { if (!reviews.length) { fail(el); return; } render(el, reviews); })
      .catch(function () { fail(el); el.__loaded = false; });
  }

  function bootEach(sel, fn) {
    var nodes = document.querySelectorAll(sel);
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.__loaded) return;
      if (!('IntersectionObserver' in window)) { fn(el); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { fn(el); io.disconnect(); } });
      }, { rootMargin: '200px' });
      io.observe(el);
    });
  }
  function boot() {
    bootEach('[data-trautis-reviews-carousel]', load);
    bootEach('[data-trautis-trust-strip]', loadStrip);
  }

  var api = {
    buildUrl: buildUrl, fetchReviews: fetchReviews,
    esc: esc, starsHtml: starsHtml, fmtDate: fmtDate, bodyHtml: bodyHtml,
    sortReviews: sortReviews, aggregate: aggregate, productRef: productRef,
    badgeHtml: badgeHtml, verifiedSealSvg: verifiedSealSvg, cardHtml: cardHtml, render: render, load: load,
    plainBody: plainBody, stripPool: stripPool, stripHtml: stripHtml, renderStrip: renderStrip, loadStrip: loadStrip
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(boot, 0); });
  }
})();
